import { useCallback, useEffect, useMemo, type ReactNode } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { authService } from "@/services/authService";
import { registerAuthLostHandler, registerGateHandler } from "@/services/axiosConfig";
import type { AuthMeResponse } from "@/types/user";

import { AuthContext, type AuthCtxValue, type AuthUser } from "./context";

export const ME_QUERY_KEY = ["auth", "me"] as const;

function deriveInitials(email: string): string {
  const local = email.split("@")[0] ?? "";
  return (local.slice(0, 2) || "?").toUpperCase();
}

function nameFromEmail(email: string): string {
  return email.split("@")[0]?.replace(/[._]/g, " ") ?? email;
}

function toUser(me: AuthMeResponse): AuthUser {
  return {
    id: me.id,
    name: nameFromEmail(me.email),
    email: me.email,
    initials: deriveInitials(me.email),
    emailVerified: me.email_verified,
    phoneVerified: me.phone_verified,
    hasPhone: me.has_phone,
    mfaEnabled: me.mfa_enabled,
    isPlatformAdmin: me.is_platform_admin,
    platformRoleSlug: me.platform_role_slug,
    permissions: me.permissions,
    manageableWorkspaces: me.manageable_workspaces,
    activeWorkspaceId: me.active_workspace_id,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: authService.me,
    retry: false,
    staleTime: 0,
  });

  const { refetch } = query;
  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Sessão perdida (refresh do axios falhou) → zera o /me sem refetch; o
  // ProtectedLayout vê user=null e redireciona pro /login.
  useEffect(() => {
    registerAuthLostHandler(() => queryClient.setQueryData(ME_QUERY_KEY, null));
    return () => registerAuthLostHandler(null);
  }, [queryClient]);

  // SEC-024: 403 de gate (VERIFICATION_REQUIRED / MFA_SETUP_REQUIRED) no meio da
  // sessão → invalida o /me p/ refetch; o estado atualizado (email/phone/mfa) faz o
  // ProtectedLayout levar o usuário à tela de verificação/MFA em vez de um erro avulso.
  useEffect(() => {
    registerGateHandler(() => {
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    });
    return () => registerGateHandler(null);
  }, [queryClient]);

  const value = useMemo<AuthCtxValue>(
    () => ({
      user: query.data ? toUser(query.data) : null,
      loading: query.isLoading,
      refresh,
    }),
    [query.data, query.isLoading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
